import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { makeStyles, useMediaQuery, useTheme } from '@material-ui/core';
import { colorSet } from '@components/Provider';
import { CustomizedTypography } from '@components/Customized';
import * as assets from '@assets';
import { CapaPcbServiceType, RegisterRfqPcbInput } from '@generated/graphql';
import useQueryString from '@hooks/useQueryString';
import {
  PcbRequestStageEnum,
  RequestQueryString
} from '@routes/NewRequest/types';
import useRequest from '@hooks/useRequest';
import { useSelector } from 'react-redux';
import { RootState } from '@modules/index';

export interface PCBRequestProps {
  setDisabledNextButton: ((value: boolean) => void) | null;
  nextButtonAction: React.MutableRefObject<
    Partial<Record<PcbRequestStageEnum, () => void>>
  >;
}

const processList = [
  {
    title: '설계',
    description: '기판 위에 도선과 부품 배치 계획을 세웁니다.',
    enum: CapaPcbServiceType.CircuitDesign,
    image: assets.PCBProcessDesign
  },
  {
    title: '제작',
    description: '설계를 바탕으로 원판을 가공, 기판을 생산합니다.',
    enum: CapaPcbServiceType.CircuitFabrication,
    image: assets.ServiceType_Pcb_192
  },
  {
    title: '조립',
    description: '부품을 기판에 결합하는 작업입니다.',
    enum: CapaPcbServiceType.CircuitAssembly,
    image: assets.PCBProcessAssemble
  }
];

const PCBProcessSelect: React.FC<PCBRequestProps> = ({
  setDisabledNextButton,
  nextButtonAction
}) => {
  const pcbState = useSelector<RootState, RegisterRfqPcbInput | undefined>(
    (state) => state.request?.pcb ?? undefined
  );
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('sm'));
  const { onUpdateRfqInfo } = useRequest();
  const {
    query: { process },
    onUpdateQueryString
  } = useQueryString<RequestQueryString>();
  const processArray = process?.split(',') as CapaPcbServiceType[];
  const [selectedProcess, setSelectedProcess] = useState<CapaPcbServiceType[]>(
    processArray ?? []
  );
  const { selectContainer, processEntry } = usePCBProcessSelectStyles();

  const onClickProcessCard = (value: CapaPcbServiceType) => {
    if (selectedProcess.includes(value)) {
      setSelectedProcess((prev) => prev.filter((el) => el !== value));
    } else {
      setSelectedProcess((prev) => [...prev, value]);
    }
  };

  useEffect(() => {
    if (!setDisabledNextButton) return;

    if (selectedProcess.length === 0) {
      setDisabledNextButton(true);
      onUpdateQueryString({
        process: undefined
      });
    } else {
      setDisabledNextButton(false);
      onUpdateQueryString({
        process: selectedProcess.toString()
      });
    }
  }, [selectedProcess, setDisabledNextButton, onUpdateQueryString]);

  useEffect(() => {
    onUpdateRfqInfo({
      detailedServiceType: null,
      pcb: {
        ...pcbState,
        services: selectedProcess.map((process) => ({ serviceType: process }))
      }
    });
    //eslint-disable-next-line
  }, [selectedProcess, onUpdateRfqInfo]);

  useEffect(() => {
    nextButtonAction.current.PROCESS_SELECT = () => {
      onUpdateRfqInfo({
        pcb: {
          ...pcbState,
          services: selectedProcess.map((process) => ({ serviceType: process }))
        }
      });
    };
    //eslint-disable-next-line
  }, [selectedProcess, nextButtonAction, onUpdateRfqInfo]);

  return (
    <div className={selectContainer}>
      <CustomizedTypography
        variant="body1"
        color="textPrimary"
        gutterBottoms={16}
      >
        (중복선택 가능)
      </CustomizedTypography>

      <div className="select-list">
        {processList.map((process) => (
          <div
            key={process.enum}
            className={clsx([
              processEntry,
              {
                selected: selectedProcess.includes(process.enum)
              }
            ])}
            onClick={(e) => {
              onClickProcessCard(process.enum);
            }}
          >
            <div className="process-entry-text">
              <CustomizedTypography
                variant={!matches ? 'h4' : 'subtitle1'}
                color="textPrimary"
                fontWeight="700"
                gutterBottoms={matches ? 2 : undefined}
              >
                {process.title}
              </CustomizedTypography>
              <CustomizedTypography
                variant={!matches ? 'body1' : 'body2'}
                color="textPrimary"
              >
                {process.description}
              </CustomizedTypography>
            </div>
            <img
              className="process-entry-image"
              src={process.image}
              alt={process.title}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default PCBProcessSelect;

const usePCBProcessSelectStyles = makeStyles((theme) => ({
  selectContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    '& > p': {
      width: '100%'
    },
    '& .select-list': {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px'
    },
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'flex-start',
      height: '100%'
    }
  },
  processEntry: {
    width: '100%',
    height: '111px',
    padding: '24px',
    borderRadius: '16px',
    border: `1px solid ${colorSet.gray400}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: colorSet.gray000,
    textAlign: 'left',
    //eslint-disable-next-line
    ['@media (hover: hover)']: {
      '&:hover': {
        cursor: 'pointer',
        border: `2px solid ${colorSet.gray900}`,
        padding: '23px',
        [theme.breakpoints.down('sm')]: {
          border: `1px solid ${colorSet.gray400}`,
          padding: '24px'
        }
      }
    },
    '&.selected': {
      border: `2px solid ${colorSet.gray900}`,
      backgroundColor: colorSet.gray100,
      padding: '23px'
    },
    '& .process-entry-text': {
      display: 'flex',
      flexDirection: 'column'
    },
    '& .process-entry-image': {
      width: '79px',
      height: '79px',
      borderRadius: '16px',
      [theme.breakpoints.down('sm')]: {
        width: '64px',
        height: '64px'
      }
    }
  }
}));
